import * as React from 'react';
import { useCallback, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  OutlinedInput,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/system/Unstable_Grid';
import { X as XIcon } from '@phosphor-icons/react';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z as zod } from 'zod';

import type { User } from '@/types/v2/user';
import axios from '@/lib/v2/axios';

interface CreateManagerModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (user: User) => void;
  communityId: string;
}

const schema = zod.object({
  first_name: zod.string().min(1, 'A first name is required'),
  last_name: zod.string().min(1, 'A last name is required'),
  email: zod.string().email('Invalid email address'),
  send_invite: zod.boolean().optional(),
});

type Values = zod.infer<typeof schema>;

export function CreateManagerModal({
  open,
  onClose,
  onSuccess,
  communityId,
}: CreateManagerModalProps): React.JSX.Element {
  if (!communityId) {
    return <div>No community ID</div>;
  }

  const handleClose = React.useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSuccess = React.useCallback(
    (user: User) => {
      onSuccess(user);
      handleClose();
    },
    [onSuccess, handleClose]
  );

  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Values>({ resolver: zodResolver(schema) });

  const createUserRequest = useMutation({
    mutationFn: async (data: Values) => {
      await axios.get('/sanctum/csrf-cookie');
      return axios.post(`/api/v2/communities/${communityId}/managers`, data);
    },
  });

  const handleCreateManager = useCallback(
    async (data: Values) => {
      console.log('data', data);
      try {
        const response = await createUserRequest.mutateAsync(data);
        handleSuccess(response?.data);
        toast.success(`Success!`, {
          description: `${data.first_name} ${data.last_name} has been created successfully`,
          position: 'top-right',
          style: {
            background: '#107E4A',
            color: '#fff',
          },
        });
      } catch (error: any) {
        console.error(error);
        setError(error?.response?.data?.message || `An error occurred while creating the manager`);
      }
    },
    [createUserRequest, handleSuccess]
  );

  return (
    <Dialog maxWidth="xs" onClose={onClose} open={open}>
      <form onSubmit={handleSubmit(handleCreateManager)}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, minHeight: 0 }}>
          <Stack direction="row" sx={{ alignItems: 'center', flex: '0 0 auto', justifyContent: 'space-between' }}>
            <Typography variant="h6">Create Manager</Typography>
            <IconButton onClick={handleClose}>
              <XIcon />
            </IconButton>
          </Stack>
        </DialogContent>
        <Divider />
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, minHeight: 0 }}>
          <Stack direction="column" spacing={1}>
            <Typography sx={{ color: 'text.secondary' }} variant="body2">
              Creating a manager helps manage access and permissions, streamlining user management processes. Managers
              can be property managers or residents, and any user on the platform will show up here.
            </Typography>
          </Stack>
          {error ? <Alert severity="error">{error}</Alert> : null}

          <Grid container spacing={2}>
            <Grid md={6} xs={12}>
              <FormControl error={Boolean(errors.first_name)} fullWidth>
                <InputLabel required>First Name</InputLabel>
                <OutlinedInput disabled={createUserRequest.isPending} {...register('first_name')} />
                {errors.first_name ? <FormHelperText>{errors.first_name.message}</FormHelperText> : null}
              </FormControl>
            </Grid>
            <Grid md={6} xs={12}>
              <FormControl error={Boolean(errors.last_name)} fullWidth>
                <InputLabel required>Last Name</InputLabel>
                <OutlinedInput disabled={createUserRequest.isPending} {...register('last_name')} />
                {errors.last_name ? <FormHelperText>{errors.last_name.message}</FormHelperText> : null}
              </FormControl>
            </Grid>
            <Grid md={6} xs={12}>
              <FormControl error={Boolean(errors.email)} fullWidth>
                <InputLabel required>Email</InputLabel>
                <OutlinedInput disabled={createUserRequest.isPending} {...register('email')} />
                {errors.email ? <FormHelperText>{errors.email.message}</FormHelperText> : null}
              </FormControl>
            </Grid>
            <Grid alignItems="center" container justifyContent="center" md={6} xs={12}>
              <Stack direction="row" mt={2} spacing={1}>
                <FormControl>
                  <FormControlLabel control={<Checkbox {...register('send_invite')} />} label="Send an invitation" />
                </FormControl>
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button onClick={handleClose}>Cancel</Button>
          <LoadingButton
            disabled={createUserRequest.isPending}
            loading={createUserRequest.isPending}
            type="submit"
            variant="contained"
          >
            Create Manager
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}
